/**
 * Bybit REST API Functions
 *
 * REST endpoint helpers for Bybit V5 market data.
 * @see https://bybit-exchange.github.io/docs/v5/market/instrument
 */

import type {
  BybitApiResponse,
  BybitDepthData,
  BybitDepthRequest,
  BybitInstrumentInfoItem,
  BybitInstrumentInfoRequest,
  BybitKlinesRequest,
  BybitKlineTuple,
  BybitTickerData,
  BybitTickerRequest,
  BybitTradeData,
  BybitTradesRequest,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.bybit.com';

export const bybitRestEndpoints = {
  instrumentInfo: `${BASE_URL}/v5/market/instruments-info`,
  ticker: `${BASE_URL}/v5/market/tickers`,
  depth: `${BASE_URL}/v5/market/orderbook`,
  trades: `${BASE_URL}/v5/market/recent-trade`,
  klines: `${BASE_URL}/v5/market/kline`,
} as const;

/**
 * Get launched instruments information.
 * @see https://bybit-exchange.github.io/docs/v5/market/instrument
 */
export const getBybitInstrumentInfo = async (
  params: BybitInstrumentInfoRequest,
  options?: RestRequestOptions,
): Promise<
  BybitApiResponse<{
    category: string;
    list: BybitInstrumentInfoItem[];
    nextPageCursor?: string;
  }>
> => {
  const response = await get<
    BybitApiResponse<{
      category: string;
      list: BybitInstrumentInfoItem[];
      nextPageCursor?: string;
    }>,
    BybitInstrumentInfoRequest
  >({
    url: bybitRestEndpoints.instrumentInfo,
    params,
    options,
  });

  return response.data;
};

/**
 * Get ticker information for symbols.
 * @see https://bybit-exchange.github.io/docs/v5/market/tickers
 */
export const getBybitTicker = async (
  params: BybitTickerRequest,
  options?: RestRequestOptions,
): Promise<BybitApiResponse<{ category: string; list: BybitTickerData[] }>> => {
  const response = await get<
    BybitApiResponse<{ category: string; list: BybitTickerData[] }>,
    BybitTickerRequest
  >({
    url: bybitRestEndpoints.ticker,
    params,
    options,
  });

  return response.data;
};

/**
 * Retrieves the orderbook snapshot for the symbol.
 * @see https://bybit-exchange.github.io/docs/v5/market/orderbook
 */
export const getBybitDepth = async (
  params: BybitDepthRequest,
  options?: RestRequestOptions,
): Promise<BybitApiResponse<BybitDepthData>> => {
  const response = await get<BybitApiResponse<BybitDepthData>, BybitDepthRequest>({
    url: bybitRestEndpoints.depth,
    params,
    options,
  });

  return response.data;
};

/**
 * Returns the most recent public trades.
 * @see https://bybit-exchange.github.io/docs/v5/market/recent-trade
 */
export const getBybitTrades = async (
  params: BybitTradesRequest,
  options?: RestRequestOptions,
): Promise<BybitApiResponse<{ category: string; list: BybitTradeData[] }>> => {
  const response = await get<
    BybitApiResponse<{ category: string; list: BybitTradeData[] }>,
    BybitTradesRequest
  >({
    url: bybitRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};

/**
 * Requests historical klines for the symbol and interval.
 * @see https://bybit-exchange.github.io/docs/v5/market/kline
 */
export const getBybitKlines = async (
  params: BybitKlinesRequest,
  options?: RestRequestOptions,
): Promise<BybitApiResponse<{ category: string; symbol: string; list: BybitKlineTuple[] }>> => {
  const response = await get<
    BybitApiResponse<{ category: string; symbol: string; list: BybitKlineTuple[] }>,
    BybitKlinesRequest
  >({
    url: bybitRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};
